/**
 * PitchPrint Admin Tools
 * Hidden console utilities for customer support and debugging
 *
 * USAGE (from browser console):
 * - PitchPrintAdmin.unlockVariants() - Unlock variant controls
 * - PitchPrintAdmin.inspectProject(projectId) - View project data
 * - PitchPrintAdmin.fixVariant(projectId, variantId, options) - Manually set variant
 * - PitchPrintAdmin.listPending() - Show pending variant saves
 * - PitchPrintAdmin.clearPending(projectId) - Clear pending data for project
 *
 * NOT PUBLICLY DOCUMENTED - For internal support use only
 */

(function() {
  'use strict';

  const log = (...args) => console.log('[Admin Tools]', ...args);

  window.PitchPrintAdmin = {
    /**
     * Unlock all variant controls (emergency fix for stuck customers)
     */
    unlockVariants: function() {
      log('🔓 Unlocking all variant controls...');

      let unlockedCount = 0;

      // Unlock card-style variant selectors
      document.querySelectorAll('.card-p-style').forEach(el => {
        el.style.pointerEvents = '';
        el.style.opacity = '';
        el.style.filter = '';
        unlockedCount++;
      });

      // Enable hidden select elements
      document.querySelectorAll('select[name^="options"]').forEach(el => {
        el.disabled = false;
        unlockedCount++;
      });

      log(`✅ Unlocked ${unlockedCount} variant control elements`);
      log('💡 User can now change variant selections');

      return { success: true, unlockedCount };
    },

    /**
     * Inspect project data from all PitchPrint localStorage keys
     */
    inspectProject: function(projectId) {
      if (!projectId) {
        log('❌ Error: projectId required');
        log('Usage: PitchPrintAdmin.inspectProject("your-project-id")');
        return null;
      }

      log('🔍 Inspecting project:', projectId);

      const results = {
        projectId: projectId,
        found: false,
        data: {}
      };

      // Check pprint-projects (keyed by projectId)
      try {
        const pprintProjects = localStorage.getItem('pprint-projects');
        if (pprintProjects) {
          const projects = JSON.parse(pprintProjects);
          if (projects[projectId]) {
            const decoded = JSON.parse(decodeURIComponent(projects[projectId]));
            results.data.pprintProjects = decoded;
            results.found = true;
            log('✅ Found in pprint-projects:', decoded);
          }
        }
      } catch (e) {
        log('⚠️ Error reading pprint-projects:', e);
      }

      // Check pprint-sp (keyed by productId)
      try {
        const pprintSp = localStorage.getItem('pprint-sp');
        if (pprintSp) {
          const projects = JSON.parse(pprintSp);
          Object.keys(projects).forEach(productId => {
            try {
              const data = projects[productId];
              let decoded;
              if (typeof data === 'string') {
                decoded = JSON.parse(decodeURIComponent(data));
              } else {
                decoded = data;
              }

              if (decoded.projectId === projectId) {
                results.data.pprintSp = decoded;
                results.found = true;
                log('✅ Found in pprint-sp (productId:', productId + '):', decoded);
              }
            } catch (e) {
              // Skip invalid entries
            }
          });
        }
      } catch (e) {
        log('⚠️ Error reading pprint-sp:', e);
      }

      // Check pp_w2p_projects (keyed by productId)
      try {
        const ppW2pProjects = localStorage.getItem('pp_w2p_projects');
        if (ppW2pProjects) {
          const projects = JSON.parse(ppW2pProjects);
          Object.keys(projects).forEach(productId => {
            try {
              const decoded = JSON.parse(decodeURIComponent(projects[productId]));
              if (decoded.projectId === projectId) {
                results.data.ppW2pProjects = decoded;
                results.found = true;
                log('✅ Found in pp_w2p_projects (productId:', productId + '):', decoded);
              }
            } catch (e) {
              // Skip invalid entries
            }
          });
        }
      } catch (e) {
        log('⚠️ Error reading pp_w2p_projects:', e);
      }

      // Check pending queue
      try {
        const pendingKey = `pp_variant_pending_${projectId}`;
        const pending = localStorage.getItem(pendingKey);
        if (pending) {
          results.data.pending = JSON.parse(pending);
          log('📝 Found in pending queue:', results.data.pending);
        }
      } catch (e) {
        log('⚠️ Error reading pending queue:', e);
      }

      if (!results.found) {
        log('❌ Project not found in any localStorage keys');
      } else {
        log('📊 Variant data:', results.data.pprintProjects?.shopifyVariant || 'Not set');
      }

      return results;
    },

    /**
     * Manually set variant for a project (force fix)
     */
    fixVariant: function(projectId, variantId, options) {
      if (!projectId || !variantId) {
        log('❌ Error: projectId and variantId required');
        log('Usage: PitchPrintAdmin.fixVariant("project-id", "variant-id", ["Option1", "Option2", "Option3"])');
        return null;
      }

      log('🔧 Fixing variant for project:', { projectId, variantId, options });

      const variantData = {
        variantId: variantId,
        options: options || [],
        manuallyFixed: true,
        fixedAt: new Date().toISOString()
      };

      let updatedCount = 0;

      // Update pprint-projects
      try {
        const pprintProjects = localStorage.getItem('pprint-projects');
        if (pprintProjects) {
          const projects = JSON.parse(pprintProjects);
          if (projects[projectId]) {
            const decoded = JSON.parse(decodeURIComponent(projects[projectId]));
            decoded.shopifyVariant = variantData;
            projects[projectId] = encodeURIComponent(JSON.stringify(decoded));
            localStorage.setItem('pprint-projects', JSON.stringify(projects));
            updatedCount++;
            log('✅ Updated pprint-projects');
          }
        }
      } catch (e) {
        log('⚠️ Error updating pprint-projects:', e);
      }

      // Update pprint-sp
      try {
        const pprintSp = localStorage.getItem('pprint-sp');
        if (pprintSp) {
          const projects = JSON.parse(pprintSp);
          Object.keys(projects).forEach(productId => {
            try {
              const data = projects[productId];
              let decoded;
              if (typeof data === 'string') {
                decoded = JSON.parse(decodeURIComponent(data));
              } else {
                decoded = data;
              }

              if (decoded.projectId === projectId) {
                decoded.shopifyVariant = variantData;
                projects[productId] = encodeURIComponent(JSON.stringify(decoded));
                localStorage.setItem('pprint-sp', JSON.stringify(projects));
                updatedCount++;
                log('✅ Updated pprint-sp');
              }
            } catch (e) {
              // Skip invalid entries
            }
          });
        }
      } catch (e) {
        log('⚠️ Error updating pprint-sp:', e);
      }

      // Update pp_w2p_projects
      try {
        const ppW2pProjects = localStorage.getItem('pp_w2p_projects');
        if (ppW2pProjects) {
          const projects = JSON.parse(ppW2pProjects);
          Object.keys(projects).forEach(productId => {
            try {
              const decoded = JSON.parse(decodeURIComponent(projects[productId]));
              if (decoded.projectId === projectId) {
                decoded.shopifyVariant = variantData;
                projects[productId] = encodeURIComponent(JSON.stringify(decoded));
                localStorage.setItem('pp_w2p_projects', JSON.stringify(projects));
                updatedCount++;
                log('✅ Updated pp_w2p_projects');
              }
            } catch (e) {
              // Skip invalid entries
            }
          });
        }
      } catch (e) {
        log('⚠️ Error updating pp_w2p_projects:', e);
      }

      if (updatedCount > 0) {
        log(`✅ Variant fixed in ${updatedCount} localStorage keys`);
        log('💡 Reload the page to see changes');
        return { success: true, updatedCount, variantData };
      } else {
        log('❌ Project not found - could not fix variant');
        return { success: false, error: 'Project not found' };
      }
    },

    /**
     * List all pending variant saves
     */
    listPending: function() {
      log('📝 Listing all pending variant saves...');

      const pending = [];

      // Scan localStorage for pending keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('pp_variant_pending_')) {
          try {
            const data = JSON.parse(localStorage.getItem(key));
            const projectId = key.replace('pp_variant_pending_', '');
            pending.push({
              projectId: projectId,
              data: data,
              age: Date.now() - data.timestamp,
              ageMinutes: Math.round((Date.now() - data.timestamp) / 60000)
            });
          } catch (e) {
            log('⚠️ Error parsing pending data for key:', key);
          }
        }
      }

      if (pending.length === 0) {
        log('✅ No pending variant saves');
      } else {
        log(`⚠️ Found ${pending.length} pending variant saves:`);
        pending.forEach(p => {
          log(`  • Project ${p.projectId}: ${p.ageMinutes}min old, autoSaved: ${p.data.autoSaved}`);
        });
      }

      return pending;
    },

    /**
     * Clear pending data for a specific project
     */
    clearPending: function(projectId) {
      if (!projectId) {
        log('❌ Error: projectId required');
        log('Usage: PitchPrintAdmin.clearPending("your-project-id")');
        return null;
      }

      const pendingKey = `pp_variant_pending_${projectId}`;

      try {
        localStorage.removeItem(pendingKey);
        log(`✅ Cleared pending data for project: ${projectId}`);
        return { success: true };
      } catch (e) {
        log('❌ Error clearing pending data:', e);
        return { success: false, error: e.message };
      }
    },

    /**
     * Show help
     */
    help: function() {
      console.log(`
╔════════════════════════════════════════════════════════════╗
║           PitchPrint Admin Tools - Help                   ║
╚════════════════════════════════════════════════════════════╝

AVAILABLE COMMANDS:

🔓 PitchPrintAdmin.unlockVariants()
   Unlock all variant controls (emergency fix for stuck customers)

🔍 PitchPrintAdmin.inspectProject(projectId)
   View complete project data from all localStorage keys
   Example: PitchPrintAdmin.inspectProject("abc123")

🔧 PitchPrintAdmin.fixVariant(projectId, variantId, options)
   Manually set variant for a project
   Example: PitchPrintAdmin.fixVariant("abc123", "46406130401557", ["Double Sided", "Circle Shape", "12\\" Mini"])

📝 PitchPrintAdmin.listPending()
   Show all pending variant saves (waiting for PitchPrint localStorage)

🧹 PitchPrintAdmin.clearPending(projectId)
   Clear pending data for specific project
   Example: PitchPrintAdmin.clearPending("abc123")

ℹ️  PitchPrintAdmin.help()
   Show this help message

════════════════════════════════════════════════════════════

⚠️  IMPORTANT: These tools are for internal support use only.
    Not publicly documented.
      `);
    }
  };

  // Auto-display help on load
  log('🛠️ Admin tools loaded. Type PitchPrintAdmin.help() for usage.');

})();
